import { useState } from 'react';
import { toast } from '@pella/sash-framework';
import { useLinkedActionCheck } from '../useActions/useLinkedActionCheck';
import { useCreateRequest } from './useCreateRequest';

const LINKED_ACTION_DISABLED_MESSAGE = 'The Linked Action is disabled, so the corresponding request was not generated.';

/**
 * Hook that guards request completion against an enabled linked action. When the request's
 * action has a linked action, the user is prompted to generate a request from it before the
 * completion proceeds. A disabled linked action skips the prompt and surfaces an error instead;
 * no linked action completes straight through.
 */
export const useLinkedActionCompletion = () => {
  const checkLinkedAction = useLinkedActionCheck();
  const { mutate: createLinkedRequest } = useCreateRequest();

  // Held in an object so useState stores the callback rather than treating it as a state updater
  const [pending, setPending] = useState(
    /** @type {{ request: Pella.ProductionRequestAPI.Request, onSettled: () => void, linkedAction: {id: string, description: string} } | null} */ (
      null
    ),
  );

  /**
   * Check the request's action for an enabled linked action before completing it.
   * @param {Pella.ProductionRequestAPI.Request} request - The request being completed.
   * @param {() => void} onSettled - Performs the actual status update to Completed.
   */
  const completeWithLinkedActionCheck = async (request, onSettled) => {
    try {
      const action = await checkLinkedAction(request.actionId);
      const linkedAction = /** @type {Local.ActionWithLinkedAction} */ (/** @type {unknown} */ (action))?.linkedAction;

      if (!linkedAction) {
        onSettled();
        return;
      }

      if (!linkedAction.active) {
        onSettled();
        toast.error(LINKED_ACTION_DISABLED_MESSAGE);
        return;
      }

      setPending({ request, onSettled, linkedAction });
    } catch {
      // A failed lookup shouldn't block the user from completing their own request.
      onSettled();
    }
  };

  /** User clicked NO: complete without generating a linked request. */
  const onDecline = () => {
    pending?.onSettled();
    setPending(null);
  };

  /** User clicked YES: create the linked request and complete, independently of each other. */
  const onConfirm = () => {
    if (!pending) return;

    const { request, onSettled, linkedAction } = pending;
    createLinkedRequest({
      actionId: linkedAction.id,
      dynamicInput: request.dynamicInput,
      dynamicInputResult: request.dynamicInputResult,
      requestQty: request.requestQty,
    });
    onSettled();
    setPending(null);
  };

  return {
    isLinkedActionPromptOpen: Boolean(pending),
    linkedActionDescription: pending?.linkedAction.description,
    completeWithLinkedActionCheck,
    onDecline,
    onConfirm,
  };
};
